import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import departmentService from '../../services/departmentService';

const DepartmentHierarchy = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  useEffect(() => {
    loadDepartmentTree();
  }, []);

  const loadDepartmentTree = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getDepartmentTree();
      if (response.success) {
        setDepartments(response.data.departments || []);
        // Auto-expand root departments
        const rootIds = response.data.departments
          .filter(dept => !dept.parentId)
          .map(dept => dept.id);
        setExpandedNodes(new Set(rootIds));
      }
    } catch (error) {
      console.error('Error loading department tree:', error);
      toast.error('Failed to load department hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (departmentId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentId)) {
        newSet.delete(departmentId);
      } else {
        newSet.add(departmentId);
      }
      return newSet;
    });
  };

  const isExpanded = (departmentId) => {
    return expandedNodes.has(departmentId);
  };

  const hasChildren = (department) => {
    return department.Children && department.Children.length > 0;
  };

  const getDepartmentLevel = (department) => {
    let level = 0;
    let current = department;
    while (current.parentId) {
      level++;
      current = departments.find(d => d.id === current.parentId);
      if (!current) break;
    }
    return level;
  };

  const renderDepartmentNode = (department, level = 0) => {
    const isNodeExpanded = isExpanded(department.id);
    const hasChildDepartments = hasChildren(department);
    const employeeCount = department.Employees?.length || 0;

    return (
      <div key={department.id} className="mb-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`
            relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
            ${selectedDepartment?.id === department.id 
              ? 'bg-blue-50 border-blue-300 shadow-lg' 
              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            }
          `}
          style={{ 
            marginLeft: `${level * 40}px`,
            position: 'relative'
          }}
          onClick={() => setSelectedDepartment(department)}
        >
          {/* Connecting line for child departments */}
          {level > 0 && (
            <div 
              className="absolute left-0 top-1/2 w-4 h-0.5 bg-gray-300 transform -translate-x-4"
              style={{ left: '-16px', top: '50%' }}
            />
          )}

          {/* Expand/Collapse Button */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              {hasChildDepartments && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(department.id);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors mr-2"
                >
                  {isNodeExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              )}
              
              {/* Department Icon */}
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-3" />
              
              {/* Department Name */}
              <h3 className="text-lg font-semibold text-gray-900">
                {department.name}
              </h3>
            </div>

            {/* Status Badge */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              department.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {department.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Department Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center">
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium mr-2">
                {department.code}
              </span>
              <span className="text-gray-600">Code</span>
            </div>
            
            <div className="flex items-center">
              <UserGroupIcon className="h-4 w-4 text-gray-500 mr-1" />
              <span className="text-gray-600">{employeeCount} employees</span>
            </div>

            {department.Manager && (
              <div className="flex items-center col-span-2">
                <UserGroupIcon className="h-4 w-4 text-gray-500 mr-1" />
                <span className="text-gray-600">
                  Manager: {department.Manager.firstName} {department.Manager.lastName}
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Child Departments with visual connections */}
        {hasChildDepartments && isNodeExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            {/* Vertical connecting line */}
            <div 
              className="absolute left-0 top-0 w-0.5 bg-gray-300"
              style={{ 
                left: `${level * 40 + 20}px`,
                height: '100%',
                top: '-8px'
              }}
            />
            
            {department.Children.map((childDept, index) => (
              <div key={childDept.id} className="relative">
                {renderDepartmentNode(childDept, level + 1)}
              </div>
            ))}
          </motion.div>
        )}
      </div>
    );
  };

  const renderDepartmentDetails = () => {
    if (!selectedDepartment) {
      return (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No department selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Select a department from the hierarchy to view details.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedDepartment.name}
            </h2>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            selectedDepartment.isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {selectedDepartment.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Department Code</dt>
                <dd className="text-sm text-gray-900">{selectedDepartment.code}</dd>
              </div>
              {selectedDepartment.description && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="text-sm text-gray-900">{selectedDepartment.description}</dd>
                </div>
              )}
              {selectedDepartment.location && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="text-sm text-gray-900">{selectedDepartment.location}</dd>
                </div>
              )}
              {selectedDepartment.costCenter && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cost Center</dt>
                  <dd className="text-sm text-gray-900">{selectedDepartment.costCenter}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Management & Structure */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Management & Structure</h3>
            <dl className="space-y-3">
              {selectedDepartment.Manager ? (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Manager</dt>
                  <dd className="text-sm text-gray-900">
                    {selectedDepartment.Manager.firstName} {selectedDepartment.Manager.lastName}
                  </dd>
                </div>
              ) : (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Manager</dt>
                  <dd className="text-sm text-gray-500">Not assigned</dd>
                </div>
              )}
              {selectedDepartment.Parent ? (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Parent Department</dt>
                  <dd className="text-sm text-gray-900">{selectedDepartment.Parent.name}</dd>
                </div>
              ) : (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Parent Department</dt>
                  <dd className="text-sm text-gray-500">None (Root Department)</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Sub-departments</dt>
                <dd className="text-sm text-gray-900">
                  {selectedDepartment.Children?.length || 0} sub-departments
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Employees</dt>
                <dd className="text-sm text-gray-900">
                  {selectedDepartment.Employees?.length || 0} employees
                </dd>
              </div>
            </dl>
          </div>

          {/* Financial Information */}
          {(selectedDepartment.budget || selectedDepartment.establishedDate) && (
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Financial Information</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedDepartment.budget && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Budget</dt>
                    <dd className="text-sm text-gray-900">
                      ${parseFloat(selectedDepartment.budget).toLocaleString()}
                    </dd>
                  </div>
                )}
                {selectedDepartment.establishedDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Established Date</dt>
                    <dd className="text-sm text-gray-900">
                      {new Date(selectedDepartment.establishedDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading department hierarchy...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Department Tree */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Organization Chart</h3>
          <button
            onClick={loadDepartmentTree}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Refresh
          </button>
        </div>
        
        <div className="space-y-2">
          {departments
            .filter(dept => !dept.parentId)
            .map(dept => renderDepartmentNode(dept))
          }
        </div>

        {departments.length === 0 && (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No departments</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create departments to see the organization chart.
            </p>
          </div>
        )}
      </div>

      {/* Department Details */}
      <div>
        {renderDepartmentDetails()}
      </div>
    </div>
  );
};

export default DepartmentHierarchy; 